import { useContext, useEffect, useRef, useState } from "react";
import "../../../css/wallet/Token/ReceiveToken.css";
import { useTranslation } from "react-i18next";
import { WalletContext } from "../WalletProvider";
import QRCodeStyling from "qr-code-styling";
import CopyAddress from "./ReceiveToken/CopyAddress";
import AddBankCard from "./AddBankCard";


function ReceiveToken({ theme, setReceiveStatus, token }) {
  const [active, setActive] = useState(false);
  const { tokensInfo } = useContext(WalletContext);
  const { t } = useTranslation();
  const qrCodeDivRef = useRef(null);
  const [copyAddressStatus, setCopyAddressStatus] = useState(false);
  const [selectedUSDTNetwork, setSelectedUSDTNetwork] = useState("TRC20");
  const [selectedBankCard, setSelectedBankCard] = useState(null);
  const [showAddCard, setShowAddCard] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const tomanInfo = tokensInfo?.[0]?.tokens?.find(t => t.symbol === "IRR");
  
  const tokenAddress = () => {
    if (!tokensInfo?.[0]?.tokens) return "";

    if (token === "trx") {
      return tokensInfo[0].tokens.find((t) => t.symbol === "TRX")?.address;
    }
    if (token === "bnb") {
      return tokensInfo[0].tokens.find((t) => t.symbol === "BNB")?.address;
    }
    if (token === "gram") {
      return tokensInfo[0].tokens.find((t) => t.symbol === "GRAM")?.address;
    }
    if (token === "usdt") {
      const usdt = tokensInfo[0].tokens.find((t) => t.symbol === "USDT");
      if (!selectedUSDTNetwork) return "";
      return (
        usdt?.networks.find(
          (n) => n.network === selectedUSDTNetwork
        )?.address || ""
      );
    }
    if (token === "irr") {
      return "";
    }
  };

  const handleTomanReceive = async () => {
    if (!tomanInfo?.has_bank_card) {
      setErrorMessage(t("Please add a bank card first"));
      setShowAddCard(true);
      return;
    }

    if (!selectedBankCard) {
      setErrorMessage(t("Please select a bank card"));
      return;
    }

    setLoading(true);
    try {
      const response = await fetch('/api/toman/deposit/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          card_id: selectedBankCard,
          amount: 0,
        }),
      });
      
      const data = await response.json();
      if (data.status === 'success') {
        setErrorMessage("");
      }
    } catch (error) {
      setErrorMessage(t("An error occurred"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    requestAnimationFrame(() => {
      setActive(true);
    });

    const address = tokenAddress();
    if (!address || token === "irr") {
      if (qrCodeDivRef.current) {
        qrCodeDivRef.current.innerHTML = "";
      }
      return;
    }

    if (!qrCodeDivRef.current) {
      return;
    }

    qrCodeDivRef.current.innerHTML = "";
    const widthSize = qrCodeDivRef.current.offsetWidth;

    const qrCode = new QRCodeStyling({
      width: widthSize,
      height: widthSize,
      data: address,
      dotsOptions: {
        type: "rounded",
      },
      cornersSquareOptions: {
        type: "extra-rounded",
      },
      cornersDotOptions: {
        type: "dot",
      },
      image: "/icons/hiket.png",
      imageOptions: {
        imageSize: 0.6,
        margin: 12,
      },
    });

    qrCode.append(qrCodeDivRef.current);
  }, [selectedUSDTNetwork, tokensInfo, token, selectedBankCard]);

  useEffect(() => {
    if (tomanInfo?.bank_cards?.length > 0) {
      const defaultCard = tomanInfo.bank_cards.find(card => card.is_default);
      if (defaultCard) {
        setSelectedBankCard(defaultCard.id);
      } else if (tomanInfo.bank_cards.length > 0) {
        setSelectedBankCard(tomanInfo.bank_cards[0].id);
      }
    }
  }, [tomanInfo]);

  const formatCardNumber = (number) => {
    if (!number) return "";
    return number.replace(/(.{4})/g, "$1 ").trim();
  };

  return (
    <div>
      <div
        className={`shadow ${theme}`}
        onClick={(e) => {
          setActive(false);
          setTimeout(() => {
            setReceiveStatus(false);
          }, 300);
        }}
      ></div>

      <div className={`tokenReceivePopup ${theme} ${active ? "active" : ""}`}>
        <div className={`tokenReceiveHeader ${theme}`}>
          {t("Receive")}
          {" " + token.toUpperCase()}
        </div>

        <div className="tokenReceiveContentDiv">

          {token === "usdt" && (
            <div className="input_div tokenReceiveNetworkDiv">
              <div className="labelDiv">{t("Network")}</div>
              <select
                className={`info_select ${theme}`}
                value={selectedUSDTNetwork}
                onChange={(e) => setSelectedUSDTNetwork(e.target.value)}
              >
                <option value="TRC20">TRON (TRC20)</option>
              </select>
            </div>
          )}

          {token === "irr" && (
            <div className="input_div tomanReceiveSection">
              {!tomanInfo?.has_bank_card ? (
                <>
                  <div className="labelDiv">
                    {t("You dont have any bank card")}
                  </div>

                  <button
                    className={`submit_button active ${theme}`}
                    onClick={() => setShowAddCard(true)}
                  >
                    {t("Add Bank Card")}
                  </button>
                </>
              ) : (
                <>
                  <div className="input_div">
                    <div className="labelDiv">
                      {t("Select Bank Card")}
                    </div>

                    <select
                      className={`info_select ${theme}`}
                      value={selectedBankCard || ""}
                      onChange={(e) => setSelectedBankCard(e.target.value)}
                    >
                      {tomanInfo.bank_cards.map((card) => (
                        <option key={card.id} value={card.id}>
                          {card.bank_name} - {formatCardNumber(card.card_number)}
                          {card.is_default && ` (${t("Default")})`}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className={`submit_button ${theme}`}
                    onClick={() => setShowAddCard(true)}
                  >
                    {t("Add New Card")}
                  </button>

                  {errorMessage && (
                    <div className={`info_input ${theme}`}>
                      {errorMessage}
                    </div>
                  )}

                  <button
                    className={`submit_button active ${theme}`}
                    onClick={handleTomanReceive}
                    disabled={loading}
                  >
                    {loading ? t("Processing...") : t("Receive Toman")}
                  </button>
                </>
              )}
            </div>
          )}

          {token !== "irr" && (
            <>
              <div className={`tokenReceiveInfoDiv ${theme}`}>
                <div className="tokenReceiveQRCodeDiv" ref={qrCodeDivRef}></div>
                <div className="addressDivFromTokenReceive">{tokenAddress()}</div>
              </div>

              <div className="tokenReceiveButtonsDiv">
                <button
                  className={`iconButton active ${theme}`}
                  onClick={() => {
                    navigator.clipboard.writeText(tokenAddress());
                    setCopyAddressStatus(true);
                  }}
                >
                  <img src="/icons/copy.png" alt="copy" />
                </button>
              </div>
            </>
          )}

          {copyAddressStatus && (
            <CopyAddress
              theme={theme}
              setCopyAddressStatus={setCopyAddressStatus}
            />
          )}
        </div>
      </div>

      {showAddCard && (
        <AddBankCard
          theme={theme}
          setShowAddCard={setShowAddCard}
          onCardAdded={() => {
            window.location.reload();
          }}
        />
      )}
    </div>
  );
}

export default ReceiveToken;